"use client";

import { useState, useEffect } from "react";
import { apiPost } from "@/lib/api";
import { useToast } from "@/components/toast-provider";
import { Modal } from "@/components/modal";
import { NumberStepper } from "@/components/number-stepper";
import { useConfirm } from "@/components/confirm-provider";
import { confirmPlaceOrder } from "@/lib/confirms";
import { parseOptionDescription, parseOptionSymbol } from "@/lib/parse-utils";
import { $n, fmtSpread } from "@/lib/format";
import { OPTION_MULTIPLIER } from "@/lib/option-multiplier";
import type { Account, VseTrade, VseLiveData } from "./types";
import type { SpreadResult } from "./scan-table";
import type { SpreadGroup } from "@/lib/classify-positions";
import { ClearableInput } from "@/components/clearable-input";

export function SpreadOrderModal({ account, spread, positionSize, legConflict, onClose, onSuccess }: {
  account: { hash: string; tokenId: number; label: string };
  spread: SpreadResult | null;
  positionSize: number;
  /** If non-null, one or both legs collide with an existing open position at
   * the same strike + P/C, in a direction that turns the opening instruction
   * into a closing one. Schwab will reject the order as "not an opening
   * order" — the modal shows a warning and re-confirms before submitting. */
  legConflict?: {
    shortConflict: boolean;
    longConflict: boolean;
    shortStrike: number;
    longStrike: number;
    putCall: "PUT" | "CALL";
    underlying: string;
  } | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const [submitting, setSubmitting] = useState(false);
  const [qty, setQty] = useState(1);
  // Editable net-credit limit per spread (per share); seeded to the current mid.
  const [price, setPrice] = useState("");
  // Order duration. DAY rests until market close; GTC persists until filled/canceled.
  const [duration, setDuration] = useState("DAY");

  // Seed Qty + price when modal opens: Qty = largest N where N × maxLossPerContract
  // ≤ Position Size; price = the spread's current mid net credit.
  useEffect(() => {
    if (spread) {
      const maxLossPerContract = Math.max(0.01, spread.maxLoss * OPTION_MULTIPLIER);
      // Seed from the spread's computed contract count (honors fixed-#-contracts
      // sizing), falling back to the budget-derived count.
      setQty(Math.max(1, spread.contracts || Math.floor(positionSize / maxLossPerContract)));
      setPrice((Math.round(spread.netCredit * 100) / 100).toFixed(2));
      setDuration("DAY");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spread]);

  if (!spread) return null;
  const s = spread;
  // Max loss follows the entered credit: width − credit (per share). Lower the
  // credit and the risk rises.
  const curCredit = parseFloat(price) || s.netCredit;
  const curMaxLoss = Math.max(0, s.spreadWidth - curCredit);

  return (
    <Modal open={!!spread} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      <div>
        <h3 className="text-sm font-semibold text-amber mb-3">Add to Position</h3>
        {legConflict && (legConflict.shortConflict || legConflict.longConflict) && (
          <div className="text-[11px] text-amber bg-amber/10 border border-amber/30 rounded px-2 py-1.5 mb-3 leading-snug">
            <span className="font-semibold">Looks like you&rsquo;re adjusting an existing position.</span>{" "}
            {legConflict.shortConflict && (
              <>You already hold a <span className="font-semibold">LONG</span> {legConflict.underlying} {legConflict.shortStrike} {legConflict.putCall} — the short leg here would be SELL_TO_CLOSE, not SELL_TO_OPEN.{" "}</>
            )}
            {legConflict.longConflict && (
              <>You already hold a <span className="font-semibold">SHORT</span> {legConflict.underlying} {legConflict.longStrike} {legConflict.putCall} — the long leg here would be BUY_TO_CLOSE, not BUY_TO_OPEN.{" "}</>
            )}
            Schwab will reject this as &ldquo;not an opening order&rdquo; unless you close the conflicting leg first.
          </div>
        )}
        <div className="text-xs space-y-2 mb-4">
          <div className="text-muted">Account: <span className="text-foreground">{account.label}</span></div>
          <div className="flex justify-between border-b border-border/50 pb-1">
            <span className="text-muted">Spread</span>
            <span className="text-foreground">
              {(() => {
                const occ = parseOptionSymbol(s.shortLeg.optionSymbol);
                const exp = occ
                  ? `${parseInt(occ.expDate.slice(5, 7))}/${parseInt(occ.expDate.slice(8, 10))}/${occ.expDate.slice(2, 4)}`
                  : "";
                const pc: "P" | "C" = s.spreadType === "bull_put" ? "P" : "C";
                return fmtSpread(s.shortLeg.symbol, { exp, strike: s.shortLeg.strike, putCall: pc }, { strike: s.longLeg.strike });
              })()}
            </span>
          </div>
          <div className="flex justify-between items-center border-b border-border/50 pb-1">
            <span className="text-green font-medium">Credit / spread</span>
            <div className="inline-flex items-center gap-1.5">
              <NumberStepper value={price} onChange={setPrice} step={0.05} min={0.01} inputMode="decimal" width={56} />
              <span className="text-muted text-[11px]">(${$n((parseFloat(price) || 0) * OPTION_MULTIPLIER)})</span>
            </div>
          </div>
          <div className="flex justify-between border-b border-border/50 pb-1">
            <span className="text-red font-medium">Max loss</span>
            <span className="text-foreground">${$n(curMaxLoss * OPTION_MULTIPLIER)}</span>
          </div>
          <div className="flex justify-between items-center border-b border-border/50 pb-1">
            <span className="text-muted">Qty</span>
            <div className="inline-flex items-center rounded border border-border overflow-hidden">
              <button
                type="button"
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light"
              >
                −
              </button>
              <ClearableInput
                type="text"
                inputMode="numeric"
                value={qty}
                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1))}
                className="w-12 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setQty(qty + 1)}
                className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light"
              >
                +
              </button>
            </div>
          </div>
          <div className="flex justify-between border-b border-border/50 pb-1">
            <span className="text-muted">Total max loss</span>
            {(() => {
              const perContract = curMaxLoss * OPTION_MULTIPLIER;
              const total = qty * perContract;
              const color = total > positionSize + perContract
                ? "text-red"
                : total > positionSize
                ? "text-amber"
                : "text-foreground";
              return <span className={`font-mono ${color}`}>${$n(total)}</span>;
            })()}
          </div>
          <div className="flex justify-between items-center pt-1">
            <span className="text-muted">Duration</span>
            <select
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
            >
              <option value="DAY">DAY</option>
              <option value="GTC">GTC</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
          >
            Cancel
          </button>
          <button
            disabled={submitting}
            onClick={async () => {
              if (legConflict && (legConflict.shortConflict || legConflict.longConflict)) {
                const lines = ["This order looks like an adjustment to an existing position:"];
                if (legConflict.shortConflict) {
                  lines.push(`  • You hold LONG ${legConflict.underlying} ${legConflict.shortStrike} ${legConflict.putCall} — the SHORT leg would close it instead of opening new.`);
                }
                if (legConflict.longConflict) {
                  lines.push(`  • You hold SHORT ${legConflict.underlying} ${legConflict.longStrike} ${legConflict.putCall} — the LONG leg would close it instead of opening new.`);
                }
                lines.push("", "Schwab will likely reject this as \"not an opening order.\" Place it anyway?");
                if (!(await confirmPlaceOrder(confirm, lines))) return;
              }
              setSubmitting(true);
              try {
                const res = await apiPost<{ ok: boolean; orderId?: string; orderStatus?: string; statusDescription?: string; error?: string }>(
                  "/vse/spread-order",
                  {
                    accountHash: account.hash,
                    tokenId: account.tokenId,
                    shortOptionSymbol: s.shortLeg.optionSymbol,
                    longOptionSymbol: s.longLeg.optionSymbol,
                    quantity: qty,
                    price: Math.max(0.01, Math.round((parseFloat(price) || s.netCredit) * 100) / 100),
                    duration: duration === "GTC" ? "GOOD_TILL_CANCEL" : "DAY",
                    vseTrack: {
                      symbol: s.shortLeg.symbol,
                      stockPrice: s.shortLeg.lastPrice,
                      spreadType: s.spreadType,
                      spreadWidth: s.spreadWidth,
                      shortStrikePrice: s.shortLeg.strike,
                      longStrikePrice: s.longLeg.strike,
                      shortPremium: (s.shortLeg.bid + s.shortLeg.ask) / 2,
                      longPremium: (s.longLeg.bid + s.longLeg.ask) / 2,
                      expirationDate: new Date(Date.now() + s.shortLeg.dte * 86400000).toISOString().split("T")[0],
                      dte: s.shortLeg.dte,
                      iv: s.shortLeg.iv,
                      probTouch: s.shortLeg.probTouch ?? null,
                      marketCap: s.shortLeg.marketCap,
                      avgDailyVolume: s.shortLeg.avgDailyVolume,
                      hvPct: s.shortLeg.hvPct,
                      hvDte: s.shortLeg.hvDte,
                      openInterest: s.shortLeg.openInterest,
                      premiumPct: s.shortLeg.premiumPct,
                    },
                  }
                );
                if (res.ok) {
                  showToast("success", `${s.spreadType === "bull_put" ? "Put" : "Call"} spread order ${res.orderStatus ?? "placed"}: ${s.shortLeg.symbol} $${s.shortLeg.strike}/$${s.longLeg.strike}`);
                  onSuccess();
                } else {
                  showToast("error", res.error ?? res.statusDescription ?? "Order failed");
                }
              } catch (e) {
                showToast("error", e instanceof Error ? e.message : "Spread order failed");
              }
              setSubmitting(false);
              onClose();
            }}
            className="text-xs px-3 py-1.5 rounded bg-green/20 text-green hover:bg-green/30 transition-colors font-medium disabled:opacity-40"
          >
            {submitting ? "Placing..." : "Place Order"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function CloseSpreadModal({ account, spread, trade, liveData, decayPct, seedAtMid, targetPriceOverride, beforePlace, onClose, onSuccess }: {
  account: Account;
  spread: SpreadGroup | null;
  trade: VseTrade | null;
  liveData: VseLiveData | null;
  decayPct: number;
  /** Seed the price field to the current net mid instead of the decay target. */
  seedAtMid?: boolean;
  /** Pre-computed take-profit target. When supplied, this is used as the seed
   *  price (so the modal matches the tiered target shown in the Exit Order
   *  cell exactly, instead of recomputing with a single decayPct). */
  targetPriceOverride?: number | null;
  /** Awaited right before the close order is placed (e.g. cancel any
   *  existing orders for this spread first). */
  beforePlace?: () => Promise<void>;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { showToast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [price, setPrice] = useState<string>("");
  const [qty, setQty] = useState(1);
  const [duration, setDuration] = useState("DAY");

  // Seed precedence: seedAtMid → explicit override → decay-target → mid fallback.
  // Exit-at-mid is the trader's "get me out now" price; the override is the
  // take-profit the agent would place, used only when mid isn't requested.
  const targetPrice =
    seedAtMid
      ? liveData
        ? Math.max(0.01, +liveData.netMid.toFixed(2))
        : null
      : targetPriceOverride != null && Number.isFinite(targetPriceOverride) && targetPriceOverride > 0
      ? Math.max(0.01, +targetPriceOverride.toFixed(2))
      : trade?.netPremium != null
      ? Math.max(0.01, +(trade.netPremium * (1 - decayPct / 100)).toFixed(2))
      : liveData
      ? Math.max(0.01, +liveData.netMid.toFixed(2))
      : null;

  // Reset/seed when the modal opens for a new spread.
  useEffect(() => {
    if (spread) {
      setQty(spread.shortLeg.contracts);
      setDuration("DAY");
      if (targetPrice != null) {
        setPrice(targetPrice.toFixed(2));
      } else {
        setPrice("");
      }
    } else {
      setPrice("");
    }
    // Only reseed when the spread itself changes, so user edits to price persist while
    // live quotes keep streaming.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spread]);

  // Quotes can land after the ticket opens. Fill mid once if the field is
  // still empty so Exit isn't stuck waiting for a re-open.
  useEffect(() => {
    if (!spread || !seedAtMid || price) return;
    if (liveData && Number.isFinite(liveData.netMid)) {
      setPrice(Math.max(0.01, +liveData.netMid.toFixed(2)).toFixed(2));
    }
  }, [spread, seedAtMid, liveData, price]);

  return (
    <Modal open={!!spread} onClose={() => { if (!submitting) onClose(); }} width="420px" zIndex={60}>
      {spread && (() => {
        const sp = spread;
        const maxQty = sp.shortLeg.contracts;
        const parsedPrice = parseFloat(price);
        const validPrice = !isNaN(parsedPrice) && parsedPrice > 0;
        const stockPrice = liveData?.underlyingPrice ?? null;
        const typeLabel = sp.putCall === "PUT" ? "Put Close" : "Call Close";
        const pcShort = sp.putCall === "PUT" ? "P" : "C";
        return (
          <div>
            <h3 className={`text-sm font-semibold mb-3 ${seedAtMid ? "text-red" : "text-green"}`}>{seedAtMid ? "Exit at mid" : "Exit at stop"} — {sp.underlying}</h3>
            <div className="text-xs space-y-2 mb-4">
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Type</span>
                <span className="text-foreground font-mono font-medium">{typeLabel}</span>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">Symbol</span>
                <span className="text-foreground font-mono font-medium">{sp.underlying}</span>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">BTC short</span>
                <span className="text-foreground font-mono text-[10px]">${$n(sp.shortLeg.strike)} {pcShort}</span>
              </div>
              <div className="flex justify-between border-b border-border/50 pb-1">
                <span className="text-muted">STC long</span>
                <span className="text-foreground font-mono text-[10px]">${$n(sp.longLeg.strike)} {pcShort}</span>
              </div>
              {liveData && (
                <div className="flex justify-between border-b border-border/50 pb-1">
                  <span className="text-muted">Bid / Ask / Mid</span>
                  <span className="font-mono">
                    <span className="text-muted">${$n(liveData.netBid)}</span>
                    {" / "}
                    <span className="text-muted">${$n(liveData.netAsk)}</span>
                    {" / "}
                    <span
                      className="text-green cursor-pointer"
                      title="Use mid"
                      onClick={() => setPrice(Math.max(0.01, +liveData.netMid.toFixed(2)).toFixed(2))}
                    >${$n(liveData.netMid)}</span>
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Net debit</span>
                <div className={`inline-flex items-center rounded border overflow-hidden ${validPrice ? "border-border" : "border-red"}`}>
                  <button type="button" onClick={() => setPrice(Math.max(0.01, (parseFloat(price) || 0) - 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="decimal"
                    value={price}
                    onChange={(e) => setPrice(e.target.value.replace(/[^0-9.]/g, ""))}
                    className={`w-16 bg-surface-light border-x border-border px-2 py-0.5 font-mono text-xs text-right focus:outline-none ${validPrice ? "text-foreground" : "text-red"}`}
                  />
                  <button type="button" onClick={() => setPrice(((parseFloat(price) || 0) + 0.01).toFixed(2))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between items-center border-b border-border/50 pb-1">
                <span className="text-muted">Qty</span>
                <div className="inline-flex items-center rounded border border-border overflow-hidden">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">−</button>
                  <ClearableInput
                    type="text"
                    inputMode="numeric"
                    value={qty}
                    onChange={(e) => setQty(Math.max(1, Math.min(maxQty, parseInt(e.target.value.replace(/[^0-9]/g, "")) || 1)))}
                    className="w-12 bg-surface-light border-x border-border px-2 py-0.5 text-foreground font-mono text-xs text-right focus:outline-none"
                  />
                  <button type="button" onClick={() => setQty(Math.min(maxQty, qty + 1))} className="px-2 py-0.5 text-muted hover:text-foreground hover:bg-surface-light">+</button>
                </div>
              </div>
              <div className="flex justify-between items-center pt-1">
                <span className="text-muted">Duration</span>
                <select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  className="bg-surface-light border border-border rounded px-2 py-0.5 text-foreground font-mono text-xs focus:outline-none focus:border-cyan"
                >
                  <option value="DAY">DAY</option>
                  <option value="GTC">GTC</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                disabled={submitting}
                className="text-xs px-3 py-1.5 rounded bg-surface-light text-muted hover:text-foreground transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submitting || !validPrice}
                onClick={async () => {
                  setSubmitting(true);
                  try {
                    if (beforePlace) await beforePlace();
                    const res = await apiPost<{ ok: boolean; orderId?: string; orderStatus?: string; statusDescription?: string; error?: string }>(
                      "/vse/spread-close",
                      {
                        accountHash: account.hashValue,
                        tokenId: account.tokenId,
                        tradeId: trade?.id ?? null,
                        shortOptionSymbol: sp.shortLeg.optionSymbol,
                        longOptionSymbol: sp.longLeg.optionSymbol,
                        quantity: qty,
                        price: parsedPrice,
                        stockPrice: stockPrice,
                        duration: duration === "GTC" ? "GOOD_TILL_CANCEL" : "DAY",
                      }
                    );
                    if (res.ok) {
                      showToast("success", `Close placed: ${sp.underlying} #${res.orderId} (${res.orderStatus})`);
                      onSuccess();
                      onClose();
                    } else {
                      showToast("error", res.error ?? res.statusDescription ?? "Close failed");
                    }
                  } catch (e) {
                    showToast("error", e instanceof Error ? e.message : "Close failed");
                  }
                  setSubmitting(false);
                }}
                className={`text-xs px-3 py-1.5 rounded font-medium disabled:opacity-40 transition-colors ${seedAtMid ? "bg-red/20 text-red hover:bg-red/30" : "bg-green/20 text-green hover:bg-green/30"}`}
              >
                {submitting ? "Placing..." : seedAtMid ? "Exit at mid" : "Exit at stop"}
              </button>
            </div>
          </div>
        );
      })()}
    </Modal>
  );
}
